import React, { useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS, FlexView, Row, Separator } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import { useStudentPtmReportGet } from 'actions/studentsv2';
import BallLoader from 'comps/ball-loader';
import { convertHTMLToPDF } from 'utils/ptm';

import ObservationCard from './observation-card';
import RealWorldProject from './real-world-project';
import SchoolHelp from './school-help';
import StrategyCard from './strategy-card';
import StudentInfo from './student-info';
import SupportCard from './support-card';

const MainContainer = styled.div`
  width: 600px;
  margin: auto;
`;

const StyledRow = styled(Row)`
  width: 85%;
  margin: auto;
  padding: 16px 12px;
  margin-top: -30px;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  border: 1px solid ${COLORS.GHOST};
`;

const StyledImage = styled.img`
  height: 40px;
  width: 40px;
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: calc(100vh - 58px);
  margin: auto;
  display: flex;
  align-items: center;
`;

const DownloadButton = styled(Button)`
  position: fixed;
  top: 56px; //* Height of header.
  right: 10px;
  height: 40px;
`;

const WrappedText = styled(Text)`
  word-wrap: break-word;
`;

const ChaptersWrapper = styled(FlexView)`
  border-radius: 16px;
  padding: 16px;
  background: ${COLORS.GRASS_XXLIGHT};
`;

export const STRATEGY_CARD_COLORS = [
  [COLORS.FLOWER_XXLIGHT, COLORS.FLOWER_XLIGHT, COLORS.FLOWER_DARK],
  [COLORS.GRASS_XXLIGHT, COLORS.GRASS_XLIGHT, COLORS.GRASS_LIGHT],
  [COLORS.SUN_XXLIGHT, COLORS.SUN_XLIGHT, COLORS.SUN_DARK],
  [COLORS.SKY_XXLIGHT, COLORS.SKY_XLIGHT, COLORS.SKY_DARK],
  [COLORS.ORANGE_XXLIGHT, COLORS.ORANGE_XLIGHT, COLORS.ORANGE_DARK],
];

export const OBSERVATION_CARD_COLORS = {
  'Strength 💪': COLORS.GRASS_XXLIGHT,
  'Growth 🚀': COLORS.SUN_XXLIGHT,
  'Focus Area 🎯': COLORS.ORANGE_XXLIGHT,
};

const PtmReport = ({ match }) => {
  const { params } = match || {};
  const { id: ptmId } = params || {};

  const { data: ptmData, get: getPtm } = useStudentPtmReportGet(ptmId);

  useEffect(() => {
    if (ptmId && !ptmData) {
      getPtm(ptmId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPtm, ptmId]);

  const { ptm_config: ptmConfig, report } = ptmData || {};
  const { config, ptm_type: ptmType } = ptmConfig || {};
  const { ptm_report_sections: ptmReportSections, late_if_minutes: lateIfMinutes } =
    config || {};

  const isUpgradePTM = ptmType && ptmType.split('_').includes('UPGRADE');
  const isOnDemandPTM = ptmType === 'ON_DEMAND';

  const {
    teacher_observation: teacherObservationsConfig,
    learning_strategy: learningStrategy,
    parent_support: parentSupport,
    school_support: schoolSupportConfig,
  } = ptmReportSections || {};

  const { subsections: schoolSupportSubSections } = schoolSupportConfig || {};
  const { homework_help: homeworkHelpConfig = {}, test_help: testHelpConfig = {} } =
    schoolSupportSubSections || {};

  const { subsections } = teacherObservationsConfig || {};
  const { focus_area: focusArea, teacher_remarks: teacherRemarks, strengths } =
    subsections || {};
  const {
    report_sections: reportSections,

    student_data: studentData,
  } = report || {};
  const {
    teacher_observation: teacherObservationReport,
    strategies: strategiesReport,
    parent_support: parentSupportReport,
    real_world_project: realWorldProjectReport,
    school_support: schoolSupport,
    upcoming_chapters: chapters,
  } = reportSections || {};

  const { upcoming_chapters: upcomingChapters } = chapters || {};

  const { homework_help: homeWorkHelpReport, test_help: testHelpReport } =
    schoolSupport || {};

  const {
    strengths: strengthsReport = {},
    focus_area: focusAreaReport,
    teacher_remarks: teacherRemarksReport,
  } = teacherObservationReport || {};

  const { strategies: strategyConfig } = learningStrategy || {};
  const { report_tag: remarkTag } = teacherRemarks || {};
  const { options: parentSupportOptions } = parentSupport || {};
  const { areas: strengthAreas = {} } = strengths || {};
  const { areas: focusAreas = [], report_tag: focusAreaReportTag } = focusArea || {};
  const selectedFocusArea = focusAreas?.find(
    ({ area_key: areaKey }) => areaKey === focusAreaReport,
  );
  const { area_label: focusAreaLabel, report_statement: focusRportStatement } =
    selectedFocusArea || {};
  const {
    first_name: firstName,
    grade,
    attendance_data: attendanceData,
    homework_data: homeWorkData,
    late_class_data: lateClassData = [],
    real_world_project: realWorldProjectInfo,
  } = studentData || {};

  const { classes_completed: classesCompleted, weeks_completed: weeksCompleted } =
    attendanceData || {};

  const { sheets_assigned: sheetsAssigned, sheets_not_completed: sheetsNotCompleted } =
    homeWorkData || {};

  const INFO_LABEL_MAP = {
    '<classes_completed>': classesCompleted,
    '<weeks_completed>': weeksCompleted,
    '<sheets_not_completed>': sheetsNotCompleted,
    '<sheets_assigned>': sheetsAssigned,
    '<no_of_late_classes>': lateClassData.length,
    '<late_if_minutes>': lateIfMinutes,
  };

  const getReportHeadings = useCallback(
    key => {
      const heading = ptmReportSections ? ptmReportSections[key] : '';
      const { steps_label: stepsLabel = '' } = heading || {};

      return stepsLabel;
    },
    [ptmReportSections],
  );

  const renderStrengthCards = useCallback(() => {
    return (
      strengthsReport &&
      Object.entries(strengthsReport).map(([key, value]) => {
        if (value != null && value >= 0) {
          const selectedData = strengthAreas.find(
            ({ area_key: areaKey }) => areaKey === key,
          );
          const { area_label: areaLabel, options } = selectedData || {};
          const selectedOption = (Array.isArray(options) && options[value]) || [];
          const { report_statement: reportStatement, report_tag: reportTag } =
            selectedOption || {};

          return (
            <React.Fragment key={areaLabel}>
              <ObservationCard
                key={key}
                color={OBSERVATION_CARD_COLORS[reportTag] || COLORS.GRASS_XXLIGHT}
                heading={areaLabel}
                tag={reportTag}
                info={reportStatement}
              />
              <Separator height={16} transparent />
            </React.Fragment>
          );
        }

        return null;
      })
    );
  }, [strengthAreas, strengthsReport]);

  const renderStrategyCard = useCallback(() => {
    let count = -1;

    return (
      strategiesReport &&
      Object.entries(strategiesReport).map(([key, value]) => {
        const selectedStrategyIdxArr = value;
        const { options, category_label: categoryLabel } = strategyConfig[key] || {};

        const renderData = options.filter((item, idx) =>
          selectedStrategyIdxArr.includes(idx),
        );

        if (!renderData.length) return null;
        count++;

        return (
          <React.Fragment key={key}>
            <StrategyCard
              key={key}
              heading={categoryLabel}
              color={STRATEGY_CARD_COLORS[count % 5]}
              data={renderData}
            />
            <Separator height={16} transparent />
          </React.Fragment>
        );
      })
    );
  }, [strategiesReport, strategyConfig]);

  const getPtmReportPDF = () => {
    const htmlElement = document.getElementById('ptm-report-main-container');

    const htmlToPDFData = {
      htmlElement,
      filename: 'ptm-report',
      imgFormat: 'PNG',
      jsPDFOptions: {
        orientation: 'p',
        unit: 'px',
        format: 'a4',
      },
    };

    convertHTMLToPDF(htmlToPDFData);
  };

  if (!ptmData) {
    return (
      <LoaderContainer>
        <BallLoader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <DownloadButton label="Download!" size="small" onClick={getPtmReportPDF} />
      <MainContainer id="ptm-report-main-container">
        <StudentInfo name={firstName} grade={grade} />
        <FlexView>
          <Separator height={32} transparent />

          <FlexView>
            {realWorldProjectInfo && (isUpgradePTM || isOnDemandPTM) && (
              <>
                <FlexView flexDirection="row" justifyContent="space-between">
                  <Text variant="h4">{getReportHeadings('real_world_project')}</Text>
                </FlexView>
                <Separator height={16} transparent />
                <RealWorldProject
                  realWorldProjectInfo={realWorldProjectInfo}
                  realWorldProjectReport={realWorldProjectReport}
                />
              </>
            )}
            {(isUpgradePTM || isOnDemandPTM) && (
              <>
                <FlexView flexDirection="row" justifyContent="space-between">
                  <Text variant="h4">{getReportHeadings('school_support')}</Text>
                </FlexView>
                <Separator height={16} transparent />
                <SchoolHelp
                  homeworkHelpConfig={homeworkHelpConfig}
                  testHelpConfig={testHelpConfig}
                  homeWorkHelpReport={homeWorkHelpReport}
                  testHelpReport={testHelpReport}
                  studentData={studentData}
                  ptmType={ptmType}
                />
              </>
            )}

            <FlexView flexDirection="row" justifyContent="space-between">
              <Text variant="h4">{getReportHeadings('teacher_observation')}</Text>
            </FlexView>
            <Separator height={16} transparent />
            {renderStrengthCards()}
            <ObservationCard
              color={COLORS.ORANGE_XXLIGHT}
              heading={focusAreaLabel}
              tag={focusAreaReportTag}
              info={focusRportStatement}
            />
            <Separator height={16} transparent />
            {!!teacherRemarksReport && (
              <ObservationCard
                color={COLORS.FLOWER_XXLIGHT}
                heading={''}
                tag={remarkTag}
                info={teacherRemarksReport}
              />
            )}
            <Separator height={32} transparent />
            <FlexView flexDirection="row" justifyContent="space-between">
              <Text variant="h4">{getReportHeadings('learning_strategy')}</Text>
            </FlexView>
            <Separator height={16} transparent />
            {renderStrategyCard()}
            <Separator height={16} transparent />
            {isOnDemandPTM && (
              <>
                <Text variant="h4">{getReportHeadings('upcoming_chapters')}</Text>
                <Separator height={16} transparent />
                <ChaptersWrapper>
                  <WrappedText renderAs="body2">{upcomingChapters}</WrappedText>
                </ChaptersWrapper>

                <Separator height={32} transparent />
              </>
            )}
            {Array.isArray(parentSupportReport) && !!parentSupportReport.length && (
              <FlexView flexDirection="row" justifyContent="space-between">
                <Text variant="h4">{getReportHeadings('parent_support')}</Text>
              </FlexView>
            )}

            <Separator height={16} transparent />
            {Array.isArray(parentSupportReport) &&
              parentSupportReport.map(selectedIdx => {
                const {
                  option_label: optionlabel,
                  report_statement: reporStatement,
                } = parentSupportOptions[selectedIdx];
                let { option_info: optionInfo } = parentSupportOptions[selectedIdx];

                Object.keys(INFO_LABEL_MAP).forEach(key => {
                  optionInfo = optionInfo.replace(key, INFO_LABEL_MAP[key]);
                });

                return (
                  <React.Fragment key={optionlabel}>
                    <SupportCard
                      heading={optionlabel}
                      info={optionInfo}
                      statement={reporStatement}
                    />
                    <Separator height={16} transparent />
                  </React.Fragment>
                );
              })}

            <Separator height={32} transparent />
          </FlexView>
        </FlexView>
      </MainContainer>
    </>
  );
};

export default withRouter(PtmReport);
